import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Autocomplete,
  ButtonBase,
  Chip,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { DesktopTimePicker } from "@mui/x-date-pickers/DesktopTimePicker";
import moment from "moment-timezone";

import ArrowBackIcon from "../../../assets/icons/back.svg";
import PaymentIcon from "../../../assets/icons/payment.svg";
import "./book-services.scss";
import {
  getBookingAppointmentLeadById,
  getProviderServicesList,
  getProviderServiceById,
  bookPackageFromLead,
} from "../../../api/provider-section/provider-service-management";

//  MUI Icons (for Lead info + fields)
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import { KeyboardArrowDownOutlined } from "@mui/icons-material";

type SubItem = { id: number; name: string; price: number };
type Service = {
  id: number;
  name: string;
  price: number;
  needtype?: string;
  serviceId?: number;
  subServices?: SubItem[];
  merchandise?: SubItem[];
};
type SelectedExtra = {
  parentServiceId: number;
  parentServiceName: string;
  itemType: "subService" | "merchandise";
  id: number;
  name: string;
  price: number;
};

const autocompleteDropdownSx = {
  "& .MuiAutocomplete-listbox": {
    fontFamily: "inherit",
    fontSize: "14px",
    fontWeight: 400,
  },
  "& .MuiAutocomplete-option": {
    fontFamily: "inherit",
    fontSize: "14px",
    fontWeight: 400,
    minHeight: "40px",
    alignItems: "center",
  },
};

function toServiceDateTime(
  date: Dayjs | null,
  time: Dayjs | null,
  timezone: string
): string {
  if (!date || !time) return "";

  const formattedDate = date.format("YYYY-MM-DD");
  const formattedTime = time.format("hh:mm A");

  const t = formattedTime.trim();
  let hours: number;
  let minutes: number;
  const pmMatch = t.match(/^(\d{1,2}):(\d{2})\s*PM$/i);
  const amMatch = t.match(/^(\d{1,2}):(\d{2})\s*AM$/i);
  const plainMatch = t.match(/^(\d{1,2}):(\d{2})/);

  if (pmMatch) {
    hours = parseInt(pmMatch[1], 10) % 12 + 12;
    minutes = parseInt(pmMatch[2], 10);
  } else if (amMatch) {
    hours = parseInt(amMatch[1], 10) % 12;
    minutes = parseInt(amMatch[2], 10);
  } else if (plainMatch) {
    hours = parseInt(plainMatch[1], 10);
    minutes = parseInt(plainMatch[2], 10);
    if (hours < 12 && /pm/i.test(t)) hours += 12;
  } else {
    return `${formattedDate}T12:00:00`;
  }

  const h = String(hours).padStart(2, "0");
  const m = String(minutes).padStart(2, "0");
  return moment
    .tz(
      `${formattedDate}T${h}:${m}:00`,
      "YYYY-MM-DDTHH:mm:ss",
      timezone
    )
    .format("YYYY-MM-DDTHH:mm:ss");
}

function mapProviderServiceFromList(item: any): Service {
  const service = item?.service ?? item;
  const id = item?.id ?? service?.id;
  const name = item?.serviceName ?? service?.name ?? item?.name ?? "";
  const needtype = item?.needtype ?? item?.needType ?? service?.needtype ?? service?.needType;
  const basePriceRaw = item?.basePrice;
  const basePrice = Number(
    typeof basePriceRaw === "object" && basePriceRaw != null
      ? basePriceRaw.min ?? basePriceRaw.max
      : basePriceRaw ?? item?.price ?? service?.basePrice ?? 0
  );
  const serviceId = item?.serviceId ?? service?.id;

  return {
    id: Number(id),
    name: String(name),
    price: Number.isFinite(basePrice) ? basePrice : 0,
    needtype: needtype ? String(needtype) : undefined,
    serviceId: serviceId != null ? Number(serviceId) : undefined,
    subServices: [],
    merchandise: [],
  };
}

/** ---------------- Types ---------------- */
type LeadAddress = {
  street?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  country?: string;
};
type LeadData = {
  id: number;
  leadName: string;
  email: string;
  phoneNumber: string;
  address?: LeadAddress;
};

interface BookServiceProps {
  leadId: number;
  onBack: () => void;
}

/**
 * Screenshot shows required → keeping true.
 */
const REQUIRE_DATE = false;
const REQUIRE_TIME = false;
const REQUIRE_LOCATION = true;

const bookServiceSchema = Yup.object({
  services: Yup.array()
    .of(
      Yup.object({
        id: Yup.number().required(),
        name: Yup.string().required(),
        price: Yup.number().required(),
      })
    )
    .min(1, "Please select at least one service.")
    .required("Please select at least one service."),

  subServices: Yup.array().of(
    Yup.object({
      id: Yup.number().required(),
      parentServiceId: Yup.number().required(),
      price: Yup.number().required(),
    })
  ),

  merchandise: Yup.array().of(
    Yup.object({
      id: Yup.number().required(),
      parentServiceId: Yup.number().required(),
      price: Yup.number().required(),
    })
  ),

  serviceDate: REQUIRE_DATE
    ? Yup.mixed().nullable().required("Service date is required.")
    : Yup.mixed().nullable(),

  serviceTime: REQUIRE_TIME
    ? Yup.mixed().nullable().required("Service time is required.")
    : Yup.mixed().nullable(),

  serviceLocation: REQUIRE_LOCATION
    ? Yup.string()
      .trim()
      .max(256, "Service location cannot exceed 256 characters.")
      .required("Service location is required.")
    : Yup.string()
      .trim()
      .max(256, "Service location cannot exceed 256 characters.")
      .nullable(),
});

const BookService: React.FC<BookServiceProps> = ({ leadId, onBack }) => {
  const [lead, setLead] = useState<LeadData | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [pageLoading, setPageLoading] = useState(true);

  const [snack, setSnack] = useState<{
    open: boolean;
    type: "success" | "error";
    message: string;
  }>({ open: false, type: "success", message: "" });

  useEffect(() => {
    const load = async () => {
      setPageLoading(true);
      try {
        const [leadRes, listRes] = await Promise.all([
          getBookingAppointmentLeadById(leadId),
          getProviderServicesList({
            page: 1,
            limit: 10,
            offset: 0,
            search: "",
          }),
        ]);

        const raw =
          leadRes?.data?.success &&
          (leadRes?.data?.data?.lead ?? leadRes?.data?.data);

        const mapped: LeadData | null = raw
          ? {
            id: raw.id,
            leadName: raw.name ?? raw.leadName ?? "",
            email: raw.email ?? "",
            phoneNumber: raw.phone ?? raw.phoneNumber ?? "",
            address: raw.address
              ? {
                street: [
                  raw.address.street_line_1 || raw.address.street,
                  raw.address.street_line_2,
                ]
                  .filter(Boolean)
                  .join(", ") || "",
                city: raw.address.city ?? "",
                state: raw.address.state ?? "",
                zipcode: raw.address.zipcode ?? "",
                country: raw.address.country ?? "",
              }
              : undefined,
          }
          : null;

        setLead(mapped);

        const list =
          listRes?.data?.data?.data ??
          listRes?.data?.data ??
          (Array.isArray(listRes?.data) ? listRes.data : []);

        const serviceList = Array.isArray(list)
          ? list.map(mapProviderServiceFromList)
          : [];

        setServices(serviceList);
      } catch (e) {
        setLead(null);
        setServices([]);
        setSnack({
          open: true,
          type: "error",
          message: "Failed to load lead/services. Please try again.",
        });
      } finally {
        setPageLoading(false);
      }
    };

    load();
  }, [leadId]);

  const formik = useFormik({
    initialValues: {
      services: [] as Service[],
      subServices: [] as SelectedExtra[],
      merchandise: [] as SelectedExtra[],
      serviceDate: null as Dayjs | null,
      serviceTime: null as Dayjs | null,
      serviceLocation: "",
    },
    validationSchema: bookServiceSchema,
    onSubmit: async (values, helpers) => {
      const isOnlyPreneedLink = values.services.length > 0 && values.services.every((s) => s.needtype === "pre_need");
      console.log("Is Only preneed", isOnlyPreneedLink);
      const timezone =
        Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";
      const serviceDateTime = toServiceDateTime(
        values.serviceDate,
        values.serviceTime,
        timezone
      );
      const serviceLocation = values.serviceLocation?.trim() || "";

      const servicesPayload = values.services.map((s) => ({
        partnerServiceId: s.id,
        subServiceIds: values.subServices
          .filter((x) => x.parentServiceId === s.id)
          .map((x) => x.id),
        merchandiseIds: values.merchandise
          .filter((x) => x.parentServiceId === s.id)
          .map((x) => x.id),
      }));

      try {
        const res = await bookPackageFromLead(
          leadId,
          {
            services: servicesPayload,
            serviceDateTime,
            serviceLocation,
          },
          {
            timezone: timezone,
          }
        );

        const success = res?.data?.success;
        const dataMessage = res?.data?.data?.message;

        if (success) {
          helpers.resetForm();

          setSnack({
            open: true,
            type: "success",
            message:
              dataMessage ||
              (isOnlyPreneedLink
                ? "Confirmation link has been successfully sent to the customer."
                : "Payment link has been successfully sent to the customer."),
          });
        } else {
          setSnack({
            open: true,
            type: "error",
            message:
              res?.data?.message ||
              dataMessage ||
              (isOnlyPreneedLink
                ? "Failed to send confirmation link. Please try again."
                : "Failed to send payment link. Please try again."),
          });
        }
      } catch (e: any) {
        setSnack({
          open: true,
          type: "error",
          message:
            e?.response?.data?.message ||
            e?.response?.data?.data?.message ||
            (isOnlyPreneedLink
              ? "Something went wrong while sending the confirmation link."
              : "Something went wrong while sending the payment link."),
        });
      } finally {
        helpers.setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    const selected = formik.values.services;
    if (!selected.length) return;

    let cancelled = false;

    const loadExtrasForService = async (svc: Service) => {
      const hasExtras =
        (svc.subServices?.length ?? 0) > 0 ||
        (svc.merchandise?.length ?? 0) > 0;

      if (hasExtras) return;

      try {
        const res = await getProviderServiceById(svc.id);
        if (cancelled) return;

        const data = res?.data?.data ?? res?.data;
        const pricing = data?.pricing ?? data?.items ?? [];

        const subServices: SubItem[] = pricing
          .filter(
            (p: any) =>
              p?.type === "sub_service" ||
              p?.type === "subService" ||
              String(p?.type).toLowerCase().includes("sub")
          )
          .map((x: any) => ({
            id: Number(x?.id),
            name: String(x?.name ?? ""),
            price: Number(x?.providerPrice ?? x?.price ?? 0) || 0,
          }));

        const merchandise: SubItem[] = pricing
          .filter(
            (p: any) =>
              p?.type === "merchandise" ||
              String(p?.type).toLowerCase().includes("merchandise")
          )
          .map((x: any) => ({
            id: Number(x?.id),
            name: String(x?.name ?? ""),
            price: Number(x?.providerPrice ?? x?.price ?? 0) || 0,
          }));

        const updated = { ...svc, subServices, merchandise };

        setServices((prev) =>
          prev.map((s) => (s.id === svc.id ? updated : s))
        );

        formik.setFieldValue(
          "services",
          formik.values.services.map((s) => (s.id === svc.id ? updated : s))
        );
      } catch {
        // Non-blocking; sub/merchandise remain empty
      }
    };

    (async () => {
      for (const svc of selected) {
        await loadExtrasForService(svc);
        if (cancelled) break;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [formik.values.services.map((s) => s.id).join(",")]);

  const selectedServices = formik.values.services;

  const addressText = useMemo(() => {
    const a = lead?.address;
    if (!a) return "-";
    const parts = [a.street, a.city, a.state, a.zipcode, a.country].filter(
      Boolean
    );
    return parts.length ? parts.join(", ") : "-";
  }, [lead]);

  const extrasOptions = useMemo<SelectedExtra[]>(() => {
    const out: SelectedExtra[] = [];

    selectedServices.forEach((svc) => {
      (svc.subServices ?? []).forEach((ss) => {
        out.push({
          parentServiceId: svc.id,
          parentServiceName: svc.name,
          itemType: "subService",
          id: ss.id,
          name: ss.name,
          price: ss.price,
        });
      });

      (svc.merchandise ?? []).forEach((m) => {
        out.push({
          parentServiceId: svc.id,
          parentServiceName: svc.name,
          itemType: "merchandise",
          id: m.id,
          name: m.name,
          price: m.price,
        });
      });
    });

    return out;
  }, [selectedServices]);

  const grandTotal = useMemo(() => {
    return selectedServices.reduce((acc, s) => {
      const subSum = formik.values.subServices
        .filter((sub) => sub.parentServiceId === s.id)
        .reduce((a, b) => a + Number(b.price || 0), 0);
      const merchSum = formik.values.merchandise
        .filter((m) => m.parentServiceId === s.id)
        .reduce((a, b) => a + Number(b.price || 0), 0);
      return acc + subSum + merchSum;
    }, 0);
  }, [selectedServices, formik.values.subServices, formik.values.merchandise]);

  const handleServicesChange = async (val: Service[]) => {
    formik.setFieldTouched("services", true, false);

    await formik.setFieldValue("services", val, false);

    const allowed = new Set(val.map((s) => s.id));

    await formik.setFieldValue(
      "subServices",
      formik.values.subServices.filter((x) => allowed.has(x.parentServiceId)),
      false
    );

    await formik.setFieldValue(
      "merchandise",
      formik.values.merchandise.filter((x) => allowed.has(x.parentServiceId)),
      false
    );

    if (val.length > 0) {
      formik.setFieldError("services", undefined);
    }

    formik.validateField("services");
  };

  if (pageLoading) {
    return (
      <Box className="book-service book-service--loading">
        <CircularProgress size={42} />
      </Box>
    );
  }

  if (!lead) {
    return (
      <Box className="book-service book-service--error">
        <Typography className="book-service__errorText">
          Lead not found.
        </Typography>
        <Button variant="outlined" onClick={onBack} sx={{ mt: 2 }}>
          Back to Leads
        </Button>
      </Box>
    );
  }

  const now = dayjs();

  const isSelectedDateToday =
    !!formik.values.serviceDate &&
    dayjs(formik.values.serviceDate).isSame(now, "day");

  const minTimeForSelectedDate = isSelectedDateToday ? now : undefined;

  const isOnlyPreneed = formik.values.services.length > 0 && formik.values.services.every((s) => s.needtype === "pre_need");

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box className="book-service">
        <Box className="book-service__scroll">
          <Box className="book-service__container">
            {/* Back */}
            <ButtonBase
              className="book-service__back"
              onClick={onBack}
              sx={{ alignSelf: "flex-start" }}
            >
              <img
                src={ArrowBackIcon}
                alt="back"
                className="book-service__backIcon"
              />
              <Typography className="book-service__backText">
                Back to Leads Detail
              </Typography>
            </ButtonBase>

            <Grid
              container
              spacing={3}
              className="book-service__grid"
              alignItems="flex-start"
            >
              {/* Left Form */}
              <Grid item xs={12} md={8}>
                <Paper elevation={0} className="book-service__card">
                  <Typography className="book-service__title">
                    Order Service &amp; Send {isOnlyPreneed ? "Confirmation" : "Payment"} Link
                  </Typography>
                  <Typography className="book-service__subtitle">
                    Configure service details and send {isOnlyPreneed ? "confirmation" : "payment"} link to the lead
                  </Typography>

                  {/* Lead info */}
                  <Paper elevation={0} className="book-service__leadInfo">
                    <Typography className="book-service__sectionTitle">
                      Lead Information
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box className="book-service__infoRow">
                          <PersonOutlineIcon className="book-service__infoIcon" />
                          <Box className="book-service__infoText">
                            <Typography className="book-service__label">
                              Name
                            </Typography>
                            <Typography className="book-service__value">
                              {lead.leadName || "-"}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box className="book-service__infoRow">
                          <MailOutlineIcon className="book-service__infoIcon" />
                          <Box className="book-service__infoText">
                            <Typography className="book-service__label">
                              Email
                            </Typography>
                            <Typography className="book-service__value">
                              {lead.email || "-"}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box className="book-service__infoRow">
                          <LocalPhoneOutlinedIcon className="book-service__infoIcon" />
                          <Box className="book-service__infoText">
                            <Typography className="book-service__label">
                              Phone Number
                            </Typography>
                            <Typography className="book-service__value">
                              {lead.phoneNumber || "-"}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box className="book-service__infoRow">
                          <LocationOnOutlinedIcon className="book-service__infoIcon" />
                          <Box className="book-service__infoText">
                            <Typography className="book-service__label">
                              Address
                            </Typography>
                            <Typography className="book-service__value">
                              {addressText}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>

                  <Divider className="book-service__divider" />

                  <form onSubmit={formik.handleSubmit}>
                    {/* Select Service */}
                    <Box className="book-service__fieldBlock">
                      <Typography className="book-service__fieldLabel" sx={{ marginBottom: '8px !important' }}>
                        Select Service{" "}
                        <span className="book-service__required">*</span>
                      </Typography>

                      <Autocomplete
                        multiple
                        className="book-service__autocomplete"
                        popupIcon={<KeyboardArrowDownOutlined />}
                        options={services}
                        value={formik.values.services}
                        onChange={(_, val) => handleServicesChange(val)}
                        onBlur={() => formik.setFieldTouched("services", true)}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(o) => {
                          let nt = "";
                          if (o.needtype === "pre_need") nt = "Preneed";
                          else if (o.needtype === "at_need") nt = "Immediate Need";
                          return nt ? `${o.name} - ${nt}` : o.name;
                        }}
                        renderTags={(value, getTagProps) =>
                          value.map((val, index) => {
                            const option = services.find(s => s.id === val.id) || val;
                            let nt = "";
                            if (option.needtype === "pre_need") nt = "Preneed";
                            else if (option.needtype === "at_need") nt = "Immediate Need";
                            const label = nt ? `${option.name} - ${nt}` : option.name;
                            return (
                              <Chip
                                variant="filled"
                                label={label}
                                size="small"
                                {...getTagProps({ index })}
                                key={val.id}
                              />
                            );
                          })
                        }
                        slotProps={{
                          paper: {
                            sx: autocompleteDropdownSx,
                          },
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder="Select service(s)"
                            error={
                              formik.touched.services &&
                              Boolean(formik.errors.services)
                            }
                            helperText={
                              formik.touched.services
                                ? (formik.errors.services as any)
                                : ""
                            }
                          />
                        )}
                      />
                    </Box>

                    {/* Sub-services */}
                    <Box className="book-service__fieldBlock">
                      <Typography className="book-service__fieldLabel" sx={{ marginBottom: '8px !important' }}>
                        Select Sub-Services
                      </Typography>

                      <Autocomplete
                        multiple
                        className="book-service__autocomplete"
                        popupIcon={<KeyboardArrowDownOutlined />}
                        disabled={!formik.values.services.length}
                        options={extrasOptions.filter(
                          (x) => x.itemType === "subService"
                        )}
                        value={formik.values.subServices}
                        onChange={(_, val) =>
                          formik.setFieldValue("subServices", val)
                        }
                        getOptionLabel={(o) =>
                          `${o.name} ($${Number(o.price || 0).toLocaleString()})`
                        }
                        isOptionEqualToValue={(a, b) =>
                          a.id === b.id &&
                          a.parentServiceId === b.parentServiceId
                        }
                        slotProps={{
                          paper: {
                            sx: autocompleteDropdownSx,
                          },
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder="Select sub-service(s)"
                          />
                        )}
                      />
                    </Box>

                    {/* Merchandise */}
                    <Box className="book-service__fieldBlock">
                      <Typography className="book-service__fieldLabel" sx={{ marginBottom: '8px !important' }}>
                        Merchandise
                      </Typography>

                      <Autocomplete
                        multiple
                        className="book-service__autocomplete"
                        popupIcon={<KeyboardArrowDownOutlined />}
                        disabled={!formik.values.services.length}
                        options={extrasOptions.filter(
                          (x) => x.itemType === "merchandise"
                        )}
                        value={formik.values.merchandise}
                        onChange={(_, val) =>
                          formik.setFieldValue("merchandise", val)
                        }
                        getOptionLabel={(o) =>
                          `${o.name} ($${Number(o.price || 0).toLocaleString()})`
                        }
                        isOptionEqualToValue={(a, b) =>
                          a.id === b.id &&
                          a.parentServiceId === b.parentServiceId
                        }
                        slotProps={{
                          paper: {
                            sx: autocompleteDropdownSx,
                          },
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder="Select merchandise"
                          />
                        )}
                      />
                    </Box>

                    {/* Date/Time */}
                    <Grid container spacing={2} className="book-service__row">
                      <Grid item xs={12} sm={6}>
                        <Box className="book-service__fieldLabelRow">
                          <CalendarMonthOutlinedIcon className="book-service__fieldIcon" />
                          <Typography className="book-service__fieldLabel">
                            Service Date{" "}
                            {REQUIRE_DATE ? (
                              <span className="book-service__required">*</span>
                            ) : null}
                          </Typography>
                        </Box>

                        <DesktopDatePicker
                          value={formik.values.serviceDate}
                          disablePast
                          minDate={dayjs()}
                          onChange={(value) => {
                            formik.setFieldValue("serviceDate", value);

                            if (value) {
                              formik.setFieldError("serviceDate", undefined);
                            }

                            const currentTime = formik.values.serviceTime;
                            const now = dayjs();
                            const isToday = value ? dayjs(value).isSame(now, "day") : false;

                            if (
                              isToday &&
                              currentTime &&
                              dayjs(currentTime).isValid()
                            ) {
                              const selectedDateTime = dayjs(value)
                                .hour(dayjs(currentTime).hour())
                                .minute(dayjs(currentTime).minute())
                                .second(0)
                                .millisecond(0);

                              if (selectedDateTime.isBefore(now)) {
                                formik.setFieldValue("serviceTime", null);
                              }
                            }
                          }}
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              onBlur: () => formik.setFieldTouched("serviceDate", true),
                              error:
                                formik.touched.serviceDate &&
                                Boolean(formik.errors.serviceDate),
                              helperText:
                                formik.touched.serviceDate && formik.errors.serviceDate
                                  ? (formik.errors.serviceDate as string)
                                  : "",
                            },
                            popper: {
                              placement: "bottom-start",
                              modifiers: [
                                {
                                  name: "preventOverflow",
                                  options: {
                                    boundary: "viewport",
                                    padding: 8,
                                  },
                                },
                                {
                                  name: "flip",
                                  options: {
                                    fallbackPlacements: ["top-start", "bottom-end", "top-end"],
                                  },
                                },
                              ],
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box className="book-service__fieldLabelRow">
                          <AccessTimeOutlinedIcon className="book-service__fieldIcon" />
                          <Typography className="book-service__fieldLabel">
                            Service Time{" "}
                            {REQUIRE_TIME ? (
                              <span className="book-service__required">*</span>
                            ) : null}
                          </Typography>
                        </Box>

                        <DesktopTimePicker
                          value={formik.values.serviceTime}
                          disablePast={isSelectedDateToday}
                          minTime={minTimeForSelectedDate}
                          closeOnSelect={false}
                          onChange={(value) => {
                            const selectedDate = formik.values.serviceDate;
                            const now = dayjs();

                            if (
                              value &&
                              selectedDate &&
                              dayjs(selectedDate).isValid()
                            ) {
                              const selectedDateTime = dayjs(selectedDate)
                                .hour(dayjs(value).hour())
                                .minute(dayjs(value).minute())
                                .second(0)
                                .millisecond(0);

                              if (selectedDateTime.isBefore(now)) {
                                formik.setFieldValue("serviceTime", null);
                                return;
                              }
                            }

                            formik.setFieldValue("serviceTime", value);

                            if (value) {
                              formik.setFieldError("serviceTime", undefined);
                            }
                          }}
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              onBlur: () => formik.setFieldTouched("serviceTime", true),
                              error:
                                formik.touched.serviceTime &&
                                Boolean(formik.errors.serviceTime),
                              helperText:
                                formik.touched.serviceTime && formik.errors.serviceTime
                                  ? (formik.errors.serviceTime as string)
                                  : "",
                            },
                            popper: {
                              placement: "bottom-start",
                              modifiers: [
                                {
                                  name: "preventOverflow",
                                  options: {
                                    boundary: "viewport",
                                    padding: 8,
                                  },
                                },
                                {
                                  name: "flip",
                                  options: {
                                    fallbackPlacements: ["top-start", "bottom-end", "top-end"],
                                  },
                                },
                              ],
                            },
                          }}
                        />
                      </Grid>
                    </Grid>

                    {/* Location */}
                    <Box className="book-service__fieldBlock">
                      <Box className="book-service__fieldLabelRow">
                        <LocationOnOutlinedIcon className="book-service__fieldIcon" />
                        <Typography className="book-service__fieldLabel">
                          Service Location{" "}
                          {REQUIRE_LOCATION ? (
                            <span className="book-service__required">*</span>
                          ) : null}
                        </Typography>
                      </Box>

                      <TextField
                        name="serviceLocation"
                        value={formik.values.serviceLocation}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        size="small"
                        fullWidth
                        placeholder="Enter service location address"
                        error={
                          formik.touched.serviceLocation &&
                          Boolean(formik.errors.serviceLocation)
                        }
                        helperText={
                          formik.touched.serviceLocation
                            ? formik.errors.serviceLocation
                            : ""
                        }
                      />
                    </Box>

                    <Box className="book-service__actions">
                      <Button
                        type="submit"
                        variant="contained"
                        className="book-service__primaryBtn"
                        disabled={formik.isSubmitting}
                      >
                        {formik.isSubmitting ? (
                          <Box className="book-service__btnLoading">
                            <CircularProgress size={18} />
                            <span>Sending...</span>
                          </Box>
                        ) : (
                          <Box
                            component="span"
                            className="book-service__primaryBtnContent"
                          >
                            {!isOnlyPreneed && (
                              <img
                                src={PaymentIcon}
                                alt=""
                                className="book-service__paymentIcon"
                              />
                            )}
                            {isOnlyPreneed ? "Send Confirmation Link" : "Send Payment Link"}
                          </Box>
                        )}
                      </Button>

                      <Button
                        variant="outlined"
                        className="book-service__secondaryBtn"
                        onClick={onBack}
                        disabled={formik.isSubmitting}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </form>
                </Paper>
              </Grid>

              {/* Right Summary */}
              <Grid item xs={12} md={4}>
                <Paper elevation={0} className="book-service__summaryCard">
                  <Typography className="book-service__summaryTitle">
                    Order Summary
                  </Typography>

                  <Box className="book-service__summaryBlock">
                    <Typography className="book-service__summarySectionLabel">
                      MAIN SERVICE
                    </Typography>

                    {!formik.values.services.length ? (
                      <Typography className="book-service__muted">
                        No service selected
                      </Typography>
                    ) : (
                      formik.values.services.map((s) => {
                        const relatedSubServices =
                          formik.values.subServices.filter(
                            (sub) => sub.parentServiceId === s.id,
                          );

                        const relatedMerchandise =
                          formik.values.merchandise.filter(
                            (m) => m.parentServiceId === s.id,
                          );

                        const subSum = relatedSubServices.reduce((a, b) => a + Number(b.price || 0), 0);
                        const merchSum = relatedMerchandise.reduce((a, b) => a + Number(b.price || 0), 0);

                        return (
                          <Box
                            key={s.id}
                            className="book-service__summaryLineBlock"
                          >
                            <Box className="book-service__summaryLine">
                              <Typography className="book-service__summaryText">
                                {s.needtype === "pre_need" ? `${s.name} - Preneed` : s.needtype === "at_need" ? `${s.name} - Immediate Need` : s.name}
                              </Typography>
                            </Box>
                            <Box className="book-service__nestedBlock">
                              {relatedSubServices.length > 0 && (
                                <Box className="book-service__nestedBlock">
                                  <Typography className="book-service__nestedLabel">
                                    Sub Services
                                  </Typography>

                                  {relatedSubServices.map((sub) => (
                                    <Box
                                      key={sub.id}
                                      className="book-service__summaryLine nested"
                                    >
                                      <Typography className="book-service__summaryText">
                                        {sub.name}
                                      </Typography>
                                      <Typography className="book-service__summaryText price">
                                        $
                                        {Number(
                                          sub.price || 0,
                                        ).toLocaleString()}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              )}
                            </Box>
                            <Box className="book-service__nestedBlock">
                              {relatedMerchandise.length > 0 && (
                                <Box className="book-service__nestedBlock">
                                  <Typography className="book-service__nestedLabel">
                                    Merchandise
                                  </Typography>

                                  {relatedMerchandise.map((m) => (
                                    <Box
                                      key={m.id}
                                      className="book-service__summaryLine nested"
                                    >
                                      <Typography className="book-service__summaryText">
                                        {m.name}
                                      </Typography>
                                      <Typography className="book-service__summaryText price">
                                        ${Number(m.price || 0).toLocaleString()}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              )}
                            </Box>
                          </Box>
                        );
                      })
                    )}
                  </Box>

                  <Divider className="book-service__summaryDivider" />

                  <Box className="book-service__totalRow">
                    <Typography className="book-service__totalLabel">
                      Total Amount
                    </Typography>
                    <Typography className="book-service__totalValue">
                      ${Number(grandTotal || 0).toLocaleString()}
                    </Typography>
                  </Box>

                  <Box className="book-service__notice">
                    <Typography className="book-service__noticeText">
                      *Payment is collected only for Immediate Need services. Preneed services payments will be finalized with the Provider.
                    </Typography>
                  </Box>

                  <Box className="book-service__paymentInfo">
                    <Typography className="book-service__paymentInfoTitle">
                      {isOnlyPreneed ? "🔗 Confirmation Link:" : "💳 Payment Link:"}
                    </Typography>
                    <Typography className="book-service__paymentInfoText">
                      A secure {isOnlyPreneed ? "confirmation" : "payment"} link will be sent to the lead&apos;s email
                      after submission.
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Box>

        <Snackbar
          open={snack.open}
          autoHideDuration={3500}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            severity={snack.type}
            variant="filled"
            onClose={() => setSnack((s) => ({ ...s, open: false }))}
          >
            {snack.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default BookService;